'use client'

import {
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import clsx from 'clsx'

import { useOverlayStack } from 'shared/contexts/OverlayStackContext'
import { useFocusTrap } from 'shared/ui/hooks/useFocusTrap'
import { ZIndex } from 'shared/ui/types'
import { isClient } from 'shared/utils/common'

import AnimatePresence from '../primitives/AnimatePresence'
import { ClientOnlyPortal } from '../primitives/ClientOnlyPortal'

import DrawerClose from './DrawerClose'
import { DrawerContext } from './DrawerContext'

import styles from './Drawer.module.scss'

type DrawerSize = 'small' | 'medium' | 'large'
type DrawerPlacement = 'left' | 'right' | 'top' | 'bottom'

const isHorizontal = (placement: DrawerPlacement) => {
  return placement === 'left' || placement === 'right'
}

const getDrawerMotion = (placement: DrawerPlacement) => {
  const transition = { duration: 0.2 }

  switch (placement) {
    case 'left':
      return {
        initial: { x: '-100%' },
        animate: { x: 0 },
        exit: { x: '-100%' },
        transition
      }
    case 'right':
      return {
        initial: { x: '100%' },
        animate: { x: 0 },
        exit: { x: '100%' },
        transition
      }
    case 'top':
      return {
        initial: { y: '-100%' },
        animate: { y: 0 },
        exit: { y: '-100%' },
        transition
      }
    case 'bottom':
      return {
        initial: { y: '100%' },
        animate: { y: 0 },
        exit: { y: '100%' },
        transition
      }
  }
}

export interface DrawerProps {
  id: string
  open: boolean
  onClose: () => void
  children: ReactNode
  closeOnBackdropClick?: boolean
  closeOnEscape?: boolean
  hideDefaultCloseButton?: boolean
  closeButtonLabel?: string
  elementToReturnFocus?: RefObject<HTMLElement | null>
  size?: DrawerSize
  placement?: DrawerPlacement
  className?: string
}

export const Drawer = ({
  id,
  open,
  onClose,
  children,
  closeOnBackdropClick = true,
  closeOnEscape = true,
  hideDefaultCloseButton = false,
  closeButtonLabel,
  elementToReturnFocus,
  size = 'medium',
  placement = 'right',
  className,
  ...props
}: DrawerProps) => {
  const [drawerElement, setDrawerElement] = useState<HTMLDivElement | null>(
    null
  )
  const backdropRef = useRef<HTMLDivElement>(null)
  const initialFocusAnchorRef = useRef<HTMLDivElement>(null)
  const { register, unregister, isTop, getZIndex } = useOverlayStack()

  const [shouldRender, setShouldRender] = useState(open)

  useFocusTrap({
    container: drawerElement,
    active: shouldRender,
    initialFocusAnchor: initialFocusAnchorRef.current ?? null,
    previouslyFocusedElement: elementToReturnFocus
  })

  useEffect(() => {
    if (!open) return

    setShouldRender(true)
    register({ id, type: 'drawer' })

    return () => unregister(id)
  }, [open, id, register, unregister])

  useEffect(() => {
    if (!open || !closeOnEscape) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isTop(id)) onClose()
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, closeOnEscape, onClose, isTop, id])

  const zIndex = useMemo(() => getZIndex(id) || ZIndex.DRAWER, [getZIndex, id])

  if (!isClient() || !shouldRender) return null

  return (
    <ClientOnlyPortal selector='#overlay-portal'>
      <DrawerContext.Provider value={{ id, onClose }}>
        <div
          className={clsx(
            styles.wrapper,
            {
              [styles.placementLeft]: placement === 'left',
              [styles.placementRight]: placement === 'right',
              [styles.placementTop]: placement === 'top',
              [styles.placementBottom]: placement === 'bottom'
            },
            className
          )}
          style={{ zIndex }}
        >
          <AnimatePresence
            className={styles.backdropMotion}
            open={open}
            onExitComplete={() => setShouldRender(false)}
            motion={{
              initial: { opacity: 0 },
              animate: { opacity: 1 },
              exit: { opacity: 0 },
              transition: { duration: 0.2 }
            }}
          >
            <div
              className={styles.backdrop}
              ref={backdropRef}
              onClick={closeOnBackdropClick ? onClose : undefined}
            />
          </AnimatePresence>
          <AnimatePresence
            className={clsx(
              styles.container,
              isHorizontal(placement) ? styles.horizontal : styles.vertical,
              styles[size]
            )}
            open={open}
            onExitComplete={() => setShouldRender(false)}
            motion={getDrawerMotion(placement)}
            id={id}
            role='dialog'
            aria-modal='true'
            aria-labelledby={`${id}-label`}
            aria-describedby={`${id}-description`}
            onClick={e => e.stopPropagation()}
            {...props}
          >
            <div className={styles.content} ref={setDrawerElement}>
              {hideDefaultCloseButton ? null : (
                <DrawerClose closeButtonLabel={closeButtonLabel} />
              )}
              {children}
            </div>
          </AnimatePresence>
        </div>
      </DrawerContext.Provider>
    </ClientOnlyPortal>
  )
}

export default Drawer
